import { Controller } from "@hotwired/stimulus"

// Renders toasts dispatched as `window.dispatchEvent(new CustomEvent('toast', {
// detail: { message, type } }))`.
//
// Several slideovers were already dispatching that event — the application and
// deposit slideovers between them fire it 15 times — but nothing had ever
// listened for it, so every one of those messages was swallowed. That included
// success confirmations for actions that really had run, such as rejecting an
// application and emailing the applicant.
//
// Attached to the shared flash container so server-rendered flashes and
// client-side toasts stack in the same place and look the same.
export default class extends Controller {
  static values = { duration: { type: Number, default: 4000 } }

  connect() {
    this.onToast = this.handleToast.bind(this)
    window.addEventListener("toast", this.onToast)
  }

  disconnect() {
    window.removeEventListener("toast", this.onToast)
  }

  handleToast(event) {
    const message = event.detail?.message
    if (!message) return

    this.element.appendChild(this.buildToast(message, event.detail?.type))
    this.reflow()
  }

  buildToast(message, type) {
    const toast = document.createElement("div")
    toast.dataset.toast = "true"
    toast.className = `fixed z-99999 border-l-4 p-4 rounded shadow-lg w-full md:max-w-md md:right-4 md:left-auto md:transform-none left-1/2 transform -translate-x-1/2 md:-translate-x-0 transition-opacity duration-300 ${this.classesFor(type)}`

    const text = document.createElement("p")
    text.className = "text-sm font-medium"
    // textContent, not innerHTML — messages can carry a property address or an
    // applicant name that came from user input.
    text.textContent = message
    toast.appendChild(text)

    setTimeout(() => this.dismiss(toast), this.durationValue)
    return toast
  }

  dismiss(toast) {
    toast.style.opacity = "0"
    setTimeout(() => {
      toast.remove()
      this.reflow()
    }, 300)
  }

  // Keeps the stack tight when several are on screen at once, and closes the
  // gap when one in the middle expires. Matches the server-rendered spacing in
  // shared/_flash.html.erb.
  reflow() {
    this.element.querySelectorAll("[data-toast]").forEach((toast, index) => {
      toast.style.top = `${5 + index * 4.5}rem`
    })
  }

  classesFor(type) {
    switch (type) {
      case "error":
        return "bg-red-100 border-red-400 text-red-700 dark:bg-red-900/30 dark:border-red-600 dark:text-red-300"
      case "warning":
        return "bg-amber-100 border-amber-400 text-amber-700 dark:bg-amber-900/30 dark:border-amber-600 dark:text-amber-300"
      case "info":
        return "bg-blue-100 border-blue-400 text-blue-700 dark:bg-blue-900/30 dark:border-blue-600 dark:text-blue-300"
      default:
        return "bg-green-100 border-green-400 text-green-700 dark:bg-green-900/30 dark:border-green-600 dark:text-green-300"
    }
  }
}
